import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Mail, MapPin, Phone, Twitter, Facebook, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Container, ArrowFwd } from "../shared/Shared";
import { subscribeNewsletter, getErrorMessage } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { COOKIE_UI } from "../../data/privacy";

const Footer = () => {
  const { t, c, lang } = useLang();
  const { COMPANY, OFFERING_GROUPS, INDUSTRIES } = c;
  const cu = COOKIE_UI[lang];
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const year = new Date().getFullYear();
  const offerings = OFFERING_GROUPS[0].items;

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success(t.footer.subscribed);
      setEmail("");
    } catch (err) {
      toast.error(getErrorMessage(err, t.common.error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative overflow-hidden bg-brand-navy text-slate-300" data-testid="site-footer">
      <div className="absolute inset-0 hero-grid opacity-20" />
      <Container className="relative">
        <div className="flex flex-col gap-8 border-b border-white/10 py-14 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="font-heading text-3xl font-bold text-white">{t.footer.newsTitle}</h3>
            <p className="mt-2 text-slate-400">{t.footer.newsDesc}</p>
          </div>
          <form onSubmit={onSubscribe} className="flex w-full max-w-md flex-col gap-2 sm:flex-row" data-testid="newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.footer.emailPh} data-testid="newsletter-email-input" className="h-12 flex-1 rounded-lg border border-white/15 bg-white/5 px-4 text-white placeholder:text-slate-500 outline-none transition-colors focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30" />
            <button type="submit" disabled={loading} data-testid="newsletter-submit-button" className="btn-primary h-12 bg-brand-orange text-white hover:bg-orange-600 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t.footer.subscribe} <ArrowFwd className="h-4 w-4" /></>}
            </button>
          </form>
        </div>

        <div className="grid gap-10 py-14 md:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="inline-block rounded-xl bg-white px-4 py-2"><img src={COMPANY.logo} alt="Jonaren Tech & Trade" className="h-12 w-auto" /></div>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate-400">{c.TMT.description.split(". ")[0]}.</p>
            <div className="mt-6 flex gap-3">
              {[{ Icon: Linkedin, href: COMPANY.linkedin, label: "LinkedIn" }, { Icon: Twitter, href: "#", label: "Twitter" }, { Icon: Facebook, href: "#", label: "Facebook" }].map(({ Icon: I, href, label }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-slate-300 transition-colors hover:border-brand-orange hover:bg-brand-orange hover:text-white"><I className="h-4 w-4" /></a>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">{t.footer.offerings}</h4>
            <ul className="mt-5 space-y-3 text-sm">{offerings.map((o) => <li key={o.slug}><Link to={`/offerings/${o.slug}`} className="transition-colors hover:text-white">{o.title}</Link></li>)}</ul>
          </div>
          <div className="lg:col-span-3">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">{t.footer.industries}</h4>
            <ul className="mt-5 space-y-3 text-sm">{INDUSTRIES.slice(0, 6).map((i) => <li key={i.slug}><Link to={`/industries/${i.slug}`} className="transition-colors hover:text-white">{i.title}</Link></li>)}</ul>
          </div>
          <div className="lg:col-span-3">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">{t.footer.contact}</h4>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal-light" /><span>{COMPANY.address}</span></li>
              <li className="flex gap-3"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal-light" /><a href={COMPANY.phoneHref} className="hover:text-white" dir="ltr">{COMPANY.phone}</a></li>
              <li className="flex gap-3"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal-light" /><a href={`mailto:${COMPANY.email}`} className="hover:text-white">{COMPANY.email}</a></li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 py-6 text-xs text-slate-500 sm:flex-row">
          <p>&copy; {year} {COMPANY.name}. {t.footer.rights}</p>
          <div className="flex gap-6">
            <Link to="/about" className="hover:text-white">{t.nav.about}</Link>
            <Link to="/careers" className="hover:text-white">{t.nav.careers}</Link>
            <Link to="/contact" className="hover:text-white">{t.nav.contact}</Link>
            <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy-link">{cu.privacy}</Link>
            <button type="button" onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))} className="hover:text-white" data-testid="footer-cookie-settings">{cu.manage}</button>
            <Link to="/admin" className="hover:text-white" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
